import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const AboutFib4Page = () => {
  const navigate = useNavigate();

  // ✅ 初期表示を即座に行うため、inViewをtrueに設定
  const [inView, setInView] = useState(true);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.2 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  // ✅ アニメーション設定を軽量化
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4 }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
              <i className="ri-heart-pulse-line text-white text-xl"></i>
            </div>
            <span className="text-xl font-bold text-gray-900">Liver Health Check</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            <span className="font-medium">戻る</span>
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 pt-32 pb-20">
        {/* ✅ 初期表示を高速化 */}
        <motion.div {...fadeInUp}>
          {/* Hero Section */}
          <div className="text-center mb-16">
            <motion.h1
              {...fadeInUp}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              FIB-4インデックス：<br />採血データから知る肝臓の健康状態
            </motion.h1>
            <motion.p
              {...fadeInUp}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto"
            >
              FIB-4インデックスは、年齢、AST、ALT、血小板数の4つの指標を組み合わせ、肝臓のしなやかさが失われる「肝線維化」のリスクを予測する世界標準の指標です。
            </motion.p>
          </div>

          {/* Dynamic Risk Meter */}
          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">リスクメーター</h2>
            <div className="relative w-full max-w-md mx-auto">
              {/* Semi-circle meter background */}
              <svg viewBox="0 0 200 120" className="w-full">
                {/* Background arc */}
                <path
                  d="M 20 100 A 80 80 0 0 1 180 100"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="20"
                  strokeLinecap="round"
                />
                {/* Low risk (green) */}
                <motion.path
                  d="M 20 100 A 80 80 0 0 1 73 35"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="20"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.3 }}
                />
                {/* Medium risk (orange) */}
                <motion.path
                  d="M 73 35 A 80 80 0 0 1 127 35"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="20"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
                {/* High risk (red) */}
                <motion.path
                  d="M 127 35 A 80 80 0 0 1 180 100"
                  fill="none"
                  stroke="#ef4444"
                  strokeWidth="20"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.7 }}
                />
                {/* Needle */}
                <motion.line
                  x1="100"
                  y1="100"
                  x2="100"
                  y2="30"
                  stroke="#1f2937"
                  strokeWidth="3"
                  strokeLinecap="round"
                  initial={{ rotate: -90 }}
                  animate={{ rotate: 0 }}
                  transition={{ duration: 1.5, delay: 0.9 }}
                  style={{ transformOrigin: '100px 100px' }}
                />
                <circle cx="100" cy="100" r="8" fill="#1f2937" />
              </svg>
              {/* Labels */}
              <div className="flex justify-between text-sm font-semibold mt-4">
                <span className="text-green-600">0</span>
                <span className="text-orange-600">1.3</span>
                <span className="text-red-600">2.67</span>
                <span className="text-gray-600">3.0+</span>
              </div>
            </div>
          </motion.div>

          {/* 4つのポイント */}
          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">4つのポイント</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* 年齢 */}
              <motion.div
                className="p-6 bg-blue-50 rounded-xl border-2 border-blue-200"
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(59, 130, 246, 0.2)' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-full">
                    <i className="ri-calendar-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">年齢</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  加齢に伴うリスク上昇の反映。年齢が高いほど、肝線維化のリスクが高まる傾向があります。
                </p>
              </motion.div>

              {/* AST & ALT */}
              <motion.div
                className="p-6 bg-green-50 rounded-xl border-2 border-green-200"
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(34, 197, 94, 0.2)' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full">
                    <i className="ri-test-tube-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">AST &amp; ALT</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  肝細胞の破壊と、線維化に伴う比率（AST &gt; ALT）の変化。肝臓の状態を示す重要な指標です。
                </p>
              </motion.div>

              {/* 血小板数 */}
              <motion.div
                className="p-6 bg-purple-50 rounded-xl border-2 border-purple-200"
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(168, 85, 247, 0.2)' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-purple-500 rounded-full">
                    <i className="ri-drop-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">血小板数</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  肝臓の硬化による脾臓の腫れと、それに伴う数値減少。血小板数の低下は肝線維化の進行を示唆します。
                </p>
              </motion.div>

              {/* 総合評価 */}
              <motion.div
                className="p-6 bg-orange-50 rounded-xl border-2 border-orange-200"
                whileHover={{ y: -5, boxShadow: '0 10px 30px rgba(249, 115, 22, 0.2)' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-full">
                    <i className="ri-calculator-line text-white text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">総合評価</h3>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  これら4つの指標を組み合わせることで、肝線維化のリスクを総合的に評価します。
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* 3段階リスク表 */}
          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">3つの目安（めやす）</h2>
            <div className="space-y-4">
              {/* 低リスク */}
              <motion.div
                className="p-6 bg-green-50 rounded-xl border-2 border-green-200"
                whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(34, 197, 94, 0.2)' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-full flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">&lt; 1.3【低リスク】</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed ml-14">
                  可能性は低いです。年1回の定期検診を継続してください。
                </p>
              </motion.div>

              {/* 中リスク */}
              <motion.div
                className="p-6 bg-orange-50 rounded-xl border-2 border-orange-200"
                whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(249, 115, 22, 0.2)' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-full flex-shrink-0">
                    <i className="ri-alert-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">1.3 ～ 2.66【中リスク】</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed ml-14">
                  疑いがあります。かかりつけ医への相談やエコー検査を検討してください。
                </p>
              </motion.div>

              {/* 高リスク */}
              <motion.div
                className="p-6 bg-red-50 rounded-xl border-2 border-red-200"
                whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(239, 68, 68, 0.2)' }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-full flex-shrink-0">
                    <i className="ri-error-warning-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">&gt; 2.67【高リスク】</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed ml-14">
                  可能性が高い状態です。速やかに肝臓専門医受診を検討して下さい
                </p>
              </motion.div>
            </div>
          </motion.div>

          {/* 重要な注意事項 */}
          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="bg-yellow-50 rounded-2xl p-8 shadow-lg border-l-4 border-yellow-500 mb-12"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-full flex-shrink-0">
                <i className="ri-alert-line text-yellow-600 text-2xl"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-4">重要な注意事項</h2>
                <ul className="space-y-2 text-sm text-gray-700 leading-relaxed">
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                    <span>この結果は診断ではありません</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                    <span>医療判断・治療の代わりになるものではありません</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-arrow-right-s-line text-yellow-600 mt-0.5"></i>
                    <span>体調不良や不安がある場合は、必ず医師・医療機関にご相談ください</span>
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="text-center mb-16"
          >
            <button
              onClick={() => navigate('/check')}
              className="px-12 py-5 bg-gradient-to-r from-blue-600 to-green-600 text-white text-lg font-bold rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              自分の数値を計算してみる
            </button>
          </motion.div>

          {/* 参考文献・出典セクション */}
          <motion.section
            ref={ref}
            {...fadeInUp}
            transition={{ delay: 0.7, duration: 0.4 }}
            className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-lg border-2 border-blue-200"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-500 rounded-full flex-shrink-0">
                <i className="ri-book-open-line text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900">参考文献・出典</h2>
            </div>

            <div className="space-y-4 mb-8">
              {/* 日本肝臓学会 */}
              <motion.a
                href="https://www.jsh.or.jp/medical/guidelines/"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-5 bg-white rounded-xl border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 cursor-pointer notranslate"
                translate="no"
                whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(59, 130, 246, 0.2)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-2 notranslate" translate="no">日本肝臓学会</h3>
                    <p className="text-sm text-gray-600 notranslate" translate="no">肝炎診療ガイドライン（C型肝炎治療ガイドライン等）</p>
                  </div>
                  <i className="ri-external-link-line text-blue-600 text-xl"></i>
                </div>
              </motion.a>

              {/* 日本消化器病学会 */}
              <motion.a
                href="https://www.jsge.or.jp/committees/guideline/"
                target="_blank"
                rel="noopener noreferrer"
                className="block p-5 bg-white rounded-xl border-2 border-blue-200 hover:border-blue-400 transition-all duration-300 cursor-pointer notranslate"
                translate="no"
                whileHover={{ y: -3, boxShadow: '0 8px 25px rgba(59, 130, 246, 0.2)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-2 notranslate" translate="no">日本消化器病学会</h3>
                    <p className="text-sm text-gray-600 notranslate" translate="no">NAFLD/NASH診療ガイドライン 2020</p>
                  </div>
                  <i className="ri-external-link-line text-blue-600 text-xl"></i>
                </div>
              </motion.a>
            </div>

            {/* 免責事項 */}
            <div className="p-5 bg-yellow-50 rounded-xl border-l-4 border-yellow-500">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-yellow-600 text-xl"></i>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed notranslate" translate="no">
                  免責事項：本サイトは診断を提供するものではなく、上記ガイドライン等に基づいたリスク予測ツールです。医療判断や治療の代わりになるものではありません。本ツールの利用により生じた損害について、当サイトは一切の責任を負いません。
                </p>
              </div>
            </div>
          </motion.section>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-green-500 rounded-lg">
                  <i className="ri-heart-pulse-line text-white text-xl"></i>
                </div>
                <span className="text-xl font-bold">Liver Health Check</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                FIB-4インデックスを用いた肝線維化リスク予測ツール
              </p>
            </div>

            <div>
              <h3 className="text-base font-bold mb-4">リンク</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate('/')}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    ホーム
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/check')}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    チェックを始める
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/terms')}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    利用規約
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => navigate('/privacy')}
                    className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                  >
                    プライバシーポリシー
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-bold mb-4">お問い合わせ</h3>
              <button
                onClick={() => navigate('/contact')}
                className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                お問い合わせフォーム
              </button>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-400">
              &copy; 2024 Liver Health Check. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutFib4Page;
